import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useReducer,
  useRef,
} from "react";
import { useQuery, useQueryClient } from "react-query";
import { getBidProduct } from "@dataLayer/product";
import { getRemainTurnQuery, getUserQuery } from "@dataLayer/user";
import {
  getMiniGameAwardListQuery,
  getMiniGameMissionsQuery,
  getMiniGamePrizeCollectionQuery,
  getMiniGameRankingQuery,
  // getMiniGameRulesQuery,
  getMiniGameSpinHistoryQuery,
  getMiniGameSpinUserQuery,
} from "@dataLayer/miniGame";
import { NewYearAudioProvider } from "@contexts/NewYearAudioProvider";
import * as actionCreators from "./actions";

const MiniGameDispatchContext = createContext(null);
const MiniGameStateContext = createContext(null);

const initialUi = {
  popupPiece: { isShowPopupPiece: false, type: null, data: null },
  popupPuzzle: { isShow: false },
  popupError: { isOpen: false, message: "" },
  popupFooter: { isShowPopup: false },
  popupEarnMore: {},
  popupIntroGift: { isOpen: false, data: null },
  popupGivePiece: {},
  popupGivePieceSuccess: {},
  popupChangeCoinsSuccess: {},
  popupMission: {},
  pieceLink: null,
  confirmPieceLink: null,
  spinLeftOverride: null,
  dataSpinLeft: {},
};

function miniGameUiReducer(state, action) {
  if (!action || !action.type) {
    console.error("Invalid action:", action);
    return state;
  }

  switch (action.type) {
    case "MINIGAME_UI/SET_POPUP_PIECE":
      return { ...state, popupPiece: action.payload || initialUi.popupPiece };
    case "MINIGAME_UI/SET_POPUP_ERROR":
      return { ...state, popupError: action.payload || initialUi.popupError };
    case "MINIGAME_UI/SET_SPIN_LEFT":
      return {
        ...state,
        spinLeftOverride: { ...(state.spinLeftOverride || {}), ...(action.payload || {}) },
        dataSpinLeft: action.payload || {},
      };
    case "MINIGAME_UI/SET_POPUP_FOOTER":
      return {
        ...state,
        popupFooter: action.payload || {},
        popupEarnMore: action.payload || {},
      };
    case "MINIGAME_UI/SET_POPUP_INTRO_GIFT":
      return { ...state, popupIntroGift: action.payload || initialUi.popupIntroGift };
    case "MINIGAME_UI/SET_POPUP_GIVE_PIECE":
      return { ...state, popupGivePiece: action.payload || {} };
    case "MINIGAME_UI/SET_POPUP_GIVE_PIECE_SUCCESS":
      return { ...state, popupGivePieceSuccess: action.payload || {} };
    case "MINIGAME_UI/SET_POPUP_CHANGE_COINS_SUCCESS":
      return { ...state, popupChangeCoinsSuccess: action.payload || {} };
    case "MINIGAME_UI/SET_POPUP_PUZZLE":
      return { ...state, popupPuzzle: action.payload || { isShow: false } };
    case "MINIGAME_UI/SET_POPUP_MISSION":
      return { ...state, popupMission: action.payload || {} };
    case "MINIGAME_UI/SET_PIECE_LINK":
      return { ...state, pieceLink: action.payload };
    case "MINIGAME_UI/SET_CONFIRM_PIECE_LINK":
      return { ...state, confirmPieceLink: action.payload };
    default:
      return state;
  }
}

function useBuildMiniGameState(ui) {
  const { data: userInfo } = useQuery(getUserQuery());
  const { data: remainTurn } = useQuery(getRemainTurnQuery());
  // const { data: bidProduct } = useQuery(getBidProduct());
  const { data: dataSpinUserMini = {} } = useQuery({
    ...getMiniGameSpinUserQuery(),
    enabled: !!userInfo,
  });
  const { data: dataMissionMini = [] } = useQuery(getMiniGameMissionsQuery());
  const { data: dataAwardListMini = [] } = useQuery(getMiniGameAwardListQuery());
  // const { data: dataRankingMini = [] } = useQuery(getMiniGameRankingQuery());
  // const { data: dataRule = [] } = useQuery(getMiniGameRulesQuery());
  const { data: dataPrizeCollectionMini = [] } = useQuery(
    getMiniGamePrizeCollectionQuery()
  );
  // const { data: dataSpinHistoryMini = {} } = useQuery(
  //   getMiniGameSpinHistoryQuery({ limit: 20 })
  // );

  const mergedSpin = {
    ...dataSpinUserMini,
    ...(ui.spinLeftOverride || {}),
  };

  return {
    account: {
      userInfo: userInfo || null,
      remainTurn: remainTurn || [],
    },
    miniGame: {
      ...ui,
      dataSpinUserMini: mergedSpin,
      dataMissionMini,
      dataAwardListMini,
      // dataRankingMini,
      // dataRule,
      dataPrizeCollectionMini,
      // dataSpinHistoryMini,
      popupPuzzle: ui.popupPuzzle,
    },
    home: {
      // bidProduct: bidProduct || [],
    },
    eventThingyan2025: {
      dataSpinLeft: ui.dataSpinLeft || mergedSpin,
    },
    bid: {
      shweHourBidCount: {
        totalBids: 0,
        reward: 0,
        isReceived: false,
      },
    },
  };
}

export function MiniGameProvider({ children }) {
  const [ui, dispatchUi] = useReducer(miniGameUiReducer, initialUi);
  const queryClient = useQueryClient();
  const state = useBuildMiniGameState(ui);
  const stateRef = useRef(state);
  stateRef.current = state;

  const dispatch = useCallback(
    (action) => {
      if (typeof action === "function") {
        return action(dispatch, () => stateRef.current, { queryClient });
      }
      return dispatchUi(action);
    },
    [queryClient]
  );

  const value = useMemo(() => dispatch, [dispatch]);

  return (
    <NewYearAudioProvider>
      <MiniGameDispatchContext.Provider value={value}>
        <MiniGameStateContext.Provider value={state}>
          {children}
        </MiniGameStateContext.Provider>
      </MiniGameDispatchContext.Provider>
    </NewYearAudioProvider>
  );
}

export function useMiniGameDispatch() {
  const ctx = useContext(MiniGameDispatchContext);
  if (!ctx) {
    throw new Error("useMiniGameDispatch must be used within MiniGameProvider");
  }
  return ctx;
}

export function useMiniGameSelector(selector) {
  const state = useContext(MiniGameStateContext);
  if (!state) {
    throw new Error("useMiniGameSelector must be used within MiniGameProvider");
  }
  return selector(state);
}

export { actionCreators };
